import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../utils/api';
import { PageHeader, SearchBar, DataTable, Badge, Pagination, Loading, formatDate, ConflictModal, usePersistedState, BackButton, COULEUR_ATTENTION, useJoursNonTravailles, libelleJourNonTravaille } from '../components/Shared';
import { useAuth } from '../context/AuthContext';

// ── Filtre multi-sélection ────────────────────────────────────
function MultiSelectFilter({ label, icon, items, selected, onChange, labelKey = 'label', valueKey = 'value' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = items.filter(item =>
    !search || String(item[labelKey]).toLowerCase().includes(search.toLowerCase())
  );

  function toggle(val) {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  }

  const isActive = selected.length > 0;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.35rem 0.75rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
          border: isActive ? '1.5px solid var(--accent-blue)' : '1px solid var(--border)',
          background: isActive ? 'rgba(59,130,246,0.12)' : 'var(--bg-card)',
          color: isActive ? 'var(--accent-blue)' : 'var(--text-secondary)',
        }}>
        {icon && <span>{icon}</span>}
        {label}
        {isActive && <span style={{ background: 'var(--accent-blue)', color: '#fff', borderRadius: '10px', padding: '0 5px', fontSize: '0.72rem', minWidth: 18, textAlign: 'center' }}>{selected.length}</span>}
        <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 300,
          background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', minWidth: '240px', maxHeight: '320px',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Recherche */}
          <div style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher…"
              style={{ width: '100%', padding: '0.3rem 0.5rem', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '0.82rem', background: 'var(--bg-elevated)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
            />
          </div>
          {/* Options */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {isActive && (
              <div
                onClick={() => { onChange([]); }}
                style={{ padding: '0.45rem 0.75rem', fontSize: '0.8rem', color: '#ef4444', cursor: 'pointer', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>
                ✕ Tout effacer
              </div>
            )}
            {filtered.length === 0 && (
              <div style={{ padding: '0.75rem', color: 'var(--text-tertiary)', fontSize: '0.82rem', textAlign: 'center' }}>Aucun résultat</div>
            )}
            {filtered.map(item => {
              const val = item[valueKey];
              const lbl = item[labelKey];
              const checked = selected.includes(val);
              return (
                <div
                  key={val}
                  onClick={() => toggle(val)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.45rem 0.75rem', cursor: 'pointer', fontSize: '0.83rem',
                    background: checked ? 'rgba(59,130,246,0.08)' : 'transparent',
                    color: checked ? 'var(--accent-blue)' : 'var(--text-primary)',
                  }}>
                  <div style={{
                    width: 15, height: 15, borderRadius: 3, flexShrink: 0,
                    border: checked ? '2px solid var(--accent-blue)' : '2px solid var(--border)',
                    background: checked ? 'var(--accent-blue)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {checked && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1, fontWeight: 700 }}>✓</span>}
                  </div>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lbl}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers date ────────────────────────────────────────────
function addWorkingDays(dateStr, nDays) {
  const d = new Date(dateStr + 'T12:00:00');
  let added = 0;
  while (added < nDays) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) added++;
  }
  return d.toISOString().split('T')[0];
}

function calcDateFin(dateDebut, dureeH) {
  if (!dateDebut || !dureeH) return '';
  const h = parseFloat(dureeH);
  if (!h || h <= 0) return dateDebut;
  const nbJours = Math.ceil(h / 7); // 7h/jour : 8h30-12h00 + 13h00-16h30
  if (nbJours <= 1) return dateDebut;
  return addWorkingDays(dateDebut, nbJours - 1);
}

// ── Planning occupation des salles ───────────────────────────
const navBtnStyle = {
  padding: '0.3rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)',
  background: 'var(--bg-elevated)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.85rem',
};

function PlanningOccupationModal({ dateRef, onClose }) {
  const [offsetWeeks, setOffsetWeeks] = useState(0);
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);

  const startRef = dateRef ? new Date(dateRef + 'T12:00:00') : new Date();
  const weekStart = new Date(startRef);
  const dow = weekStart.getDay();
  weekStart.setDate(weekStart.getDate() - (dow === 0 ? 6 : dow - 1) + offsetWeeks * 7);
  const debut = weekStart.toISOString().split('T')[0];
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 55);
  const fin = weekEnd.toISOString().split('T')[0];

  useEffect(() => {
    setLoading(true);
    apiFetch(`/salles/planning?debut=${debut}&fin=${fin}`)
      .then(d => { setPlanData(d.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [debut, fin]);

  const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '?';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', width: '680px', maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: '1rem' }}>📅 Planning d'occupation des salles</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          <button onClick={() => setOffsetWeeks(o => o - 4)} style={navBtnStyle}>‹‹ 4 sem.</button>
          <button onClick={() => setOffsetWeeks(o => o - 1)} style={navBtnStyle}>‹ 1 sem.</button>
          <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', flex: 1, textAlign: 'center' }}>
            {fmtDate(debut)} — {fmtDate(fin)}
          </span>
          <button onClick={() => setOffsetWeeks(o => o + 1)} style={navBtnStyle}>1 sem. ›</button>
          <button onClick={() => setOffsetWeeks(o => o + 4)} style={navBtnStyle}>4 sem. ››</button>
        </div>
        {loading ? <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Chargement…</p> : (
          planData?.length === 0
            ? <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Aucune salle enregistrée.</p>
            : planData?.map(salle => (
              <div key={salle.salle_id} style={{ marginBottom: '0.8rem', padding: '0.75rem 1rem', background: 'var(--bg-elevated)', borderRadius: '8px', border: `1px solid ${salle.sessions.length === 0 ? 'rgba(52,211,153,0.35)' : 'rgba(251,191,36,0.35)'}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: salle.sessions.length ? '0.5rem' : 0 }}>
                  <span style={{ display:'inline-block', width:12, height:12, borderRadius:3, background: salle.couleur || '#6b7280', flexShrink:0 }} />
                  <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-primary)' }}>{salle.nom_salle}</span>
                  {salle.capacite && <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>· {salle.capacite} places</span>}
                  {salle.adresse && <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>· {salle.adresse}</span>}
                  {salle.sessions.length === 0
                    ? <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#34d399', fontWeight: 600 }}>✓ Libre sur la période</span>
                    : <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#fbbf24', fontWeight: 600 }}>{salle.sessions.length} session(s)</span>}
                </div>
                {salle.sessions.map(s => (
                  <div key={s.session_id} style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', padding: '0.25rem 0', borderTop: '1px solid var(--border)', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ color: '#fbbf24' }}>●</span>
                    <span style={{ flex: 1 }}>{s.intitule_formation}</span>
                    {s.nom_formateur && <span style={{ color: 'var(--text-tertiary)' }}>{s.nom_formateur}</span>}
                    <span style={{ color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{fmtDate(s.date_debut)} → {fmtDate(s.date_fin)}</span>
                  </div>
                ))}
              </div>
            ))
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button className="btn-secondary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

const STATUTS_FILTRE = [
  { key: '', label: 'Toutes' },
  { key: 'en_cours', label: 'En cours' },
  { key: 'a_venir', label: 'À venir' },
  { key: 'terminee', label: 'Terminées' },
  { key: 'annulee', label: 'Annulées' },
];

export function ModalNouvelleSession({ onSave, onClose }) {
  const { data: formationsData } = useApi('/formations', { limit: 500 }, []);
  const { data: formateursData } = useApi('/formateurs', {}, []);
  const { data: sallesData } = useApi('/salles', {}, []);
  const [form, setForm] = useState({
    formation_id: '', intitule_formation: '', formateur_id: '',
    date_debut: '', date_fin: '', duree: '', horaires: '8h30-12h00 / 13h00-16h30',
    salle_id: '', lieu: '', occurrence_count: 1, annulee: false, reportee: false, notes: '',
  });
  const [nextId, setNextId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPlanning, setShowPlanning] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const salleRef = useRef(null);
  // Mode dates libres (individuelles + plages consécutives)
  const [datesLibres, setDatesLibres] = useState(false);
  const [datesSelectionnees, setDatesSelectionnees] = useState([]);
  const [inputDate, setInputDate] = useState('');
  const [plageDebut, setPlageDebut] = useState('');
  const [plageFin, setPlageFin] = useState('');
  const [plageSamedi, setPlageSamedi] = useState(false);
  const [formSearch, setFormSearch] = useState('');
  const formationsFiltered = formSearch.trim()
    ? (formationsData?.data || []).filter(f => {
        const q = formSearch.toLowerCase();
        return String(f.numero || f.formation_id).toLowerCase().includes(q)
          || (f.intitule || '').toLowerCase().includes(q);
      })
    : (formationsData?.data || []);

  // Charger le lieu par défaut depuis les paramètres société
  useEffect(() => {
    apiFetch('/parametres').then(p => {
      const lieuDefaut = p.lieu_defaut || [p.societe_nom, p.societe_adresse1, p.societe_adresse2].filter(Boolean).join(' - ');
      if (lieuDefaut) setForm(f => ({ ...f, lieu: lieuDefaut }));
    }).catch(() => {});
  }, []);

  // Auto-calcul date de fin quand date_debut ou duree changent
  useEffect(() => {
    if (form.date_debut && form.duree) {
      const fin = calcDateFin(form.date_debut, form.duree);
      if (fin) setForm(f => ({ ...f, date_fin: fin }));
    }
  }, [form.date_debut, form.duree]);

  async function chargerNextId(formation_id, date_debut) {
    if (!formation_id) { setNextId(''); return; }
    try {
      const qs = `formation_id=${formation_id}${date_debut ? `&date_debut=${date_debut}` : ''}`;
      const r = await apiFetch(`/sessions/next-id?${qs}`);
      setNextId(r.next_id);
    } catch (e) { setNextId(''); }
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputStyle = {
    width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box',
  };

  function champ(label, children, required = false) {
    return (
      <div style={{ marginBottom: '0.7rem' }}>
        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
          {label}{required && <span style={{ color: '#ef4444' }}> *</span>}
        </label>
        {children}
      </div>
    );
  }

  const joursNT = useJoursNonTravailles();                 // jours fériés + fermetures CEJ
  const [infoDates, setInfoDates] = useState('');           // alerte non bloquante : date posée sur un jour férié / fermé
  function ajouterDate() {
    if (!inputDate) return;
    const alerte = libelleJourNonTravaille(joursNT, inputDate);
    setInfoDates(alerte ? `⚠ ${new Date(inputDate + 'T00:00:00').toLocaleDateString('fr-FR')} : ${alerte} — date ajoutée, vous pouvez la retirer.` : '');
    setDatesSelectionnees(prev => [...new Set([...prev, inputDate])].sort());
    setInputDate('');
  }
  function ajouterPlage() {
    if (!plageDebut || !plageFin) return;
    const d1 = new Date(plageDebut + 'T00:00:00'), d2 = new Date(plageFin + 'T00:00:00');
    if (d1 > d2) return;
    const nouvelles = [];
    const cur = new Date(d1);
    // Date LOCALE (toISOString convertit en UTC et décalait chaque date d'un jour en France)
    const isoLocal = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    while (cur <= d2) { const j = cur.getDay(); if (j !== 0 && (j !== 6 || plageSamedi)) nouvelles.push(isoLocal(cur)); cur.setDate(cur.getDate() + 1); }
    // Les jours fériés et de fermeture CEJ sont ignorés comme les week-ends (on peut toujours les ajouter un par un)
    const ignores = nouvelles.filter(x => joursNT.has(x));
    const retenues = nouvelles.filter(x => !joursNT.has(x));
    setInfoDates(ignores.length ? `ℹ Ignoré dans la plage : ${ignores.map(x => `${new Date(x + 'T00:00:00').toLocaleDateString('fr-FR')} (${libelleJourNonTravaille(joursNT, x)})`).join(', ')}.` : '');
    setDatesSelectionnees(prev => [...new Set([...prev, ...(retenues.length ? retenues : nouvelles)])].sort());
    setPlageDebut(''); setPlageFin('');
  }
  function retirerDate(d) { setDatesSelectionnees(prev => prev.filter(x => x !== d)); }
  const fmtJour = d => new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });

  async function doCreate() {
    setSaving(true); setError('');
    try {
      const body = { ...form };
      if (datesLibres && datesSelectionnees.length > 0) {
        body.dates_specifiques = datesSelectionnees;
        body.date_debut = datesSelectionnees[0];
        body.date_fin   = datesSelectionnees[datesSelectionnees.length - 1];
      }
      const cree = await apiFetch('/sessions', { method: 'POST', body: JSON.stringify(body) });
      if (cree?.avertissement_occurrences) window.alert('⚠ Session créée, mais :\n' + cree.avertissement_occurrences);
      onSave();
    } catch (e) { setError(e.message); setSaving(false); }
  }

  async function handleSave() {
    if (!form.intitule_formation) return setError('L\'intitulé est requis');
    if (datesLibres) {
      if (datesSelectionnees.length === 0) return setError('Veuillez sélectionner au moins une date');
    } else {
      if (!form.date_debut) return setError('La date de début est requise');
    }
    setSaving(true); setError('');
    try {
      const checkDebut = datesLibres ? datesSelectionnees[0] : form.date_debut;
      const checkFin   = datesLibres ? datesSelectionnees[datesSelectionnees.length - 1] : (form.date_fin || form.date_debut);
      const { conflicts: found } = await apiFetch('/sessions/check-conflicts', {
        method: 'POST',
        body: JSON.stringify({
          date_debut:   checkDebut,
          date_fin:     checkFin,
          dates:        datesLibres ? datesSelectionnees : undefined,
          salle_id:     form.salle_id || null,
          formateur_id: form.formateur_id || null,
        }),
      });
      if (found && found.length > 0) {
        setSaving(false);
        setConflicts(found);
        setPendingSubmit(true);
        return;
      }
    } catch (err) {
      console.warn('check-conflicts échoué :', err.message);
    }
    await doCreate();
  }

  const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '?';

  return (
    <>
    {/* ── Modale confirmation conflits ─────────────────────────────────── */}
    {pendingSubmit && conflicts.length > 0 && (
      <ConflictModal
        conflicts={conflicts}
        saving={saving}
        confirmLabel="Créer quand même"
        onCancel={() => { setConflicts([]); setPendingSubmit(false); }}
        onConfirm={() => { setConflicts([]); setPendingSubmit(false); doCreate(); }}
        onModifier={conflicts.some(c => c.type === 'salle') ? () => {
          setConflicts([]); setPendingSubmit(false);
          setTimeout(() => { salleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); salleRef.current?.focus(); }, 100);
        } : undefined}
        modifierLabel="🏫 Modifier la salle"
      />
    )}
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '580px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)' }}>+ Nouvelle session</h3>

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: '0.7rem', letterSpacing: '0.06em' }}>Formation</p>
        {/* ID prévu */}
        {nextId && (
          <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--accent-blue)', borderRadius: '8px', padding: '0.6rem 1rem', marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>N° de session :</span>
            <strong style={{ color: 'var(--accent-blue)', fontFamily: 'monospace', fontSize: '1rem' }}>{nextId}</strong>
          </div>
        )}

        {champ('Formation du catalogue', (
          <>
            <input
              type="text"
              placeholder="Rechercher par numéro ou titre…"
              value={formSearch}
              onChange={e => setFormSearch(e.target.value)}
              style={{ ...inputStyle, marginBottom: 6 }}
            />
            <select
              value={form.formation_id}
              onChange={e => {
                const f = formationsData?.data?.find(f => f.formation_id === parseInt(e.target.value));
                setForm(prev => ({
                  ...prev,
                  formation_id: e.target.value,
                  intitule_formation: f ? f.intitule : prev.intitule_formation,
                  duree: f ? (f.duree_heures || prev.duree) : prev.duree,
                }));
                chargerNextId(e.target.value, form.date_debut);
                setFormSearch('');
              }}
              size={formSearch.trim() ? Math.min(formationsFiltered.length + 1, 8) : 1}
              style={{ ...inputStyle, overflowY: 'auto' }}
            >
              <option value="">— Choisir ou laisser libre —</option>
              {formationsFiltered.map(f => (
                <option key={f.formation_id} value={f.formation_id}>
                  {f.numero ? `[${f.numero}] ` : `[${f.formation_id}] `}{f.intitule?.substring(0, 60)}
                </option>
              ))}
            </select>
          </>
        ))}
        {champ('Intitulé *', <input type="text" value={form.intitule_formation} onChange={e => set('intitule_formation', e.target.value)} style={inputStyle} />, true)}

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Planification</p>

        {/* Sélecteur de mode dates */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {[{ key: false, label: '📅 Consécutives' }, { key: true, label: '📆 Dates libres' }].map(({ key, label }) => (
            <button key={String(key)} type="button" onClick={() => {
              setDatesLibres(key);
              if (key && datesSelectionnees.length === 0 && form.date_debut) setDatesSelectionnees([form.date_debut]);
            }} style={{ padding: '0.3rem 0.85rem', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', border: '1px solid var(--border)', background: datesLibres === key ? 'var(--accent-blue)' : 'var(--bg-elevated)', color: datesLibres === key ? '#fff' : 'var(--text-secondary)' }}>
              {label}
            </button>
          ))}
        </div>

        {!datesLibres ? (
          <div className="grid-2" style={{ gap: '0 0.7rem' }}>
            {champ('Date début *', <input type="date" value={form.date_debut} onChange={e => { set('date_debut', e.target.value); chargerNextId(form.formation_id, e.target.value); }} style={inputStyle} />, true)}
            {champ('Date fin', <input type="date" value={form.date_fin} onChange={e => set('date_fin', e.target.value)} style={inputStyle} />)}
            {champ('Durée (heures) — calcule la date fin', <input type="number" value={form.duree} onChange={e => set('duree', e.target.value)} style={inputStyle} placeholder="Ex: 14" />)}
            {champ('Nb occurrences', <input type="number" value={form.occurrence_count} onChange={e => set('occurrence_count', e.target.value)} min="1" style={inputStyle} />)}
          </div>
        ) : (
          <div style={{ marginBottom: '0.75rem' }}>
            {/* Ajout date unique */}
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginBottom: '0.4rem' }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', minWidth: '80px', fontWeight: 600 }}>Date</span>
              <input type="date" value={inputDate} onChange={e => setInputDate(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && ajouterDate()}
                style={{ ...inputStyle, flex: 1 }} />
              <button type="button" onClick={ajouterDate}
                style={{ padding: '0.45rem 0.8rem', borderRadius: '6px', border: 'none', background: 'var(--accent-blue)', color: '#fff', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                + Ajouter
              </button>
            </div>
            {/* Ajout plage consécutive */}
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', minWidth: '80px', fontWeight: 600 }}>Plage</span>
              <input type="date" value={plageDebut} onChange={e => setPlageDebut(e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="Du" />
              <input type="date" value={plageFin} onChange={e => setPlageFin(e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="Au" />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                <input type="checkbox" checked={plageSamedi} onChange={e => setPlageSamedi(e.target.checked)} style={{ accentColor: 'var(--accent-blue)' }} /> Sam
              </label>
              <button type="button" onClick={ajouterPlage}
                style={{ padding: '0.45rem 0.8rem', borderRadius: '6px', border: 'none', background: 'var(--accent-blue)', color: '#fff', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                + Plage
              </button>
            </div>
            {/* Liste des dates sélectionnées */}
            {infoDates && <div role="status" style={{ margin: '0.4rem 0', fontSize: '0.8rem', fontWeight: 600, color: COULEUR_ATTENTION }}>{infoDates}</div>}
            {datesSelectionnees.length > 0 ? (
              <>
                <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', maxHeight: '200px', overflowY: 'auto', marginBottom: '0.4rem' }}>
                  {datesSelectionnees.map((d, i) => (
                    <div key={d} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.3rem 0.6rem', borderBottom: i < datesSelectionnees.length - 1 ? '1px solid var(--border)' : 'none', background: i % 2 === 0 ? 'var(--bg-elevated)' : 'transparent' }}>
                      <span style={{ fontSize: '0.85rem' }}>{fmtJour(d)}{joursNT.has(d) && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', fontWeight: 600, color: COULEUR_ATTENTION }}>⚠ {libelleJourNonTravaille(joursNT, d)}</span>}</span>
                      <button type="button" onClick={() => retirerDate(d)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '1.1rem', lineHeight: 1, padding: '0 0.2rem' }}>×</button>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                  {datesSelectionnees.length} jour{datesSelectionnees.length > 1 ? 's' : ''} — du {new Date(datesSelectionnees[0] + 'T00:00:00').toLocaleDateString('fr-FR')} au {new Date(datesSelectionnees[datesSelectionnees.length - 1] + 'T00:00:00').toLocaleDateString('fr-FR')}
                </div>
              </>
            ) : (
              <div style={{ padding: '0.4rem 0', color: 'var(--text-tertiary)', fontSize: '0.82rem', fontStyle: 'italic' }}>Aucune date sélectionnée</div>
            )}
            {champ('Durée (heures)', <input type="number" value={form.duree} onChange={e => set('duree', e.target.value)} style={{ ...inputStyle, marginTop: '0.5rem' }} placeholder="Ex: 14" />)}
          </div>
        )}
        {champ('Horaires', <input type="text" value={form.horaires} onChange={e => set('horaires', e.target.value)} placeholder="Ex: 8h30-12h00 / 13h00-16h30" style={inputStyle} />)}
        {champ('Lieu', <input type="text" value={form.lieu} onChange={e => set('lieu', e.target.value)} style={inputStyle} />)}
        {champ('Salle', (
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <select ref={salleRef} value={form.salle_id} onChange={e => set('salle_id', e.target.value)} style={{ ...inputStyle, flex: 1 }}>
              <option value="">— Aucune —</option>
              {sallesData?.data?.map(s => (
                <option key={s.salle_id} value={s.salle_id}>{s.nom_salle}{s.capacite ? ` (${s.capacite} places)` : ''}</option>
              ))}
            </select>
            {form.salle_id && (() => {
              const sel = sallesData?.data?.find(s => String(s.salle_id) === String(form.salle_id));
              return sel ? <span style={{ display:'inline-block', width:22, height:22, borderRadius:4, flexShrink:0, background: sel.couleur || '#6b7280', border:'1px solid var(--border)' }} title={sel.nom_salle} /> : null;
            })()}
            <button type="button" onClick={() => setShowPlanning(true)}
              title="Voir le planning d'occupation des salles"
              style={{ padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
              📅 Planning
            </button>
          </div>
        ))}

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Formateur</p>
        {champ('Formateur', (
          <select value={form.formateur_id} onChange={e => set('formateur_id', e.target.value)} style={inputStyle}>
            <option value="">— Choisir —</option>
            {formateursData?.data?.map(f => (
              <option key={f.formateur_id} value={f.formateur_id}>
                {[f.prenom, f.nom_formateur].filter(Boolean).join(' ')}
              </option>
            ))}
          </select>
        ))}

        {champ('Notes', <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />)}

        {error && <div style={{ background: 'rgba(239,68,68,0.1)', borderRadius: '6px', padding: '0.5rem 0.8rem', color: '#f87171', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{error}</div>}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : '✓ Créer la session'}
          </button>
        </div>
      </div>
    </div>
    {showPlanning && <PlanningOccupationModal dateRef={form.date_debut} onClose={() => setShowPlanning(false)} />}
    </>
  );
}

export default function Sessions() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [search, setSearch] = usePersistedState('sessions_search', '');
  const [page, setPage] = usePersistedState('sessions_page', 1);
  const [statut, setStatut] = usePersistedState('sessions_statut', '');
  const [modal, setModal] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [archives, setArchives] = usePersistedState('sessions_archives', false);
  const [selectedFormations, setSelectedFormations] = usePersistedState('sessions_formations', []);
  const [selectedFormateurs, setSelectedFormateurs] = usePersistedState('sessions_formateurs', []);
  const [selectedClients, setSelectedClients] = usePersistedState('sessions_clients', []);

  // Chargement des listes pour les filtres (uniquement les valeurs présentes dans des sessions)
  const { data: formationsUsed } = useApi('/sessions/meta/formations', {}, [refresh]);
  const { data: formateursUsed } = useApi('/sessions/meta/formateurs', {}, [refresh]);
  const { data: clientsUsed } = useApi('/sessions/meta/clients', {}, [refresh]);

  const formationItems = (Array.isArray(formationsUsed) ? formationsUsed : []).map(f => ({
    value: f.formation_id,
    label: `[${f.numero || f.formation_id}] ${f.intitule}`,
  }));
  const formateurItems = (Array.isArray(formateursUsed) ? formateursUsed : []).map(f => ({
    value: f.formateur_id,
    label: [f.prenom, f.nom_formateur].filter(Boolean).join(' '),
  }));
  const clientItems = (Array.isArray(clientsUsed) ? clientsUsed : []).map(c => ({
    value: c.client_id,
    label: c.nom_client,
  }));

  const apiParams = {
    search, page, limit: 25, statut,
    archives: archives ? 'true' : undefined,
    formation_ids: selectedFormations.length > 0 ? selectedFormations.join(',') : undefined,
    formateur_ids: selectedFormateurs.length > 0 ? selectedFormateurs.join(',') : undefined,
    client_ids: selectedClients.length > 0 ? selectedClients.join(',') : undefined,
  };

  const { data, loading } = useApi('/sessions', apiParams, [search, page, statut, refresh, archives, selectedFormations.join(','), selectedFormateurs.join(','), selectedClients.join(',')]);

  const columns = [
    { header: 'Session', key: 'session_id', width: '130px', mobileHide: true },
    { header: 'Formation', render: r => <strong>{r.intitule_formation?.substring(0, 50)}</strong> },
    { header: 'Début', render: r => formatDate(r.date_debut), width: '100px' },
    { header: 'Fin', render: r => formatDate(r.date_fin), width: '100px', mobileHide: true },
    { header: 'Durée', render: r => `${r.duree}h`, width: '70px', mobileHide: true },
    { header: 'Formateur', key: 'nom_formateur' },
    { header: 'Salle', render: r => r.salle_id ? (
      <span style={{ display:'flex', alignItems:'center', gap:'0.35rem' }}>
        <span style={{ display:'inline-block', width:10, height:10, borderRadius:3, background: r.salle_couleur || '#6b7280', flexShrink:0 }} />
        <span style={{ fontSize:'0.82rem' }}>{r.nom_salle || r.salle_id}</span>
      </span>
    ) : null, width: '120px', mobileHide: true },
    { header: 'Inscrits', render: r => r.nb_inscrits || 0, width: '80px', mobileHide: true },
    { header: 'Statut', render: r => {
      if (r.annulee) return <Badge variant="badge-danger">Annulée</Badge>;
      if (r.reportee) return <Badge variant="badge-warning">Reportée</Badge>;
      const today = new Date().toISOString().slice(0, 10);
      const debut = (r.date_debut || '').slice(0, 10);
      const fin = (r.date_fin || '').slice(0, 10);
      if (debut > today) return <Badge variant="badge-info">À venir</Badge>;
      if (fin >= today) return <Badge variant="badge-warning">En cours</Badge>;
      return <Badge variant="badge-success">Terminée</Badge>;
    }},
  ];

  return (
    <div className="page">
      <PageHeader
        title="Sessions"
        subtitle={data ? `${data.total} sessions` : ''}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <BackButton />
            <button className="btn-secondary" onClick={() => navigate('/calendrier')} title="Voir le planning">
              📅 Planning
            </button>
            {hasRole('utilisateur') && (
              <button className="btn-primary" onClick={() => setModal(true)}>+ Nouvelle session</button>
            )}
          </div>
        }
      />

      <div className="toolbar">
        <SearchBar value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Rechercher une session..." />
        <div className="toolbar-filters">
          {STATUTS_FILTRE.map(s => (
            <button key={s.key} className={`filter-chip ${statut === s.key && !archives ? 'active' : ''}`}
              onClick={() => { setStatut(s.key); setArchives(false); setPage(1); }}>
              {s.label}
            </button>
          ))}
          <button className={`filter-chip ${archives ? 'active' : ''}`}
            onClick={() => { setArchives(a => !a); setPage(1); }}
            style={archives ? { borderColor: '#f87171', background: 'rgba(239,68,68,0.15)', color: '#f87171' } : {}}>
            🗄 Archivées
          </button>
        </div>
      </div>

      {/* Filtres formation / formateur */}
      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <MultiSelectFilter
          label="Formation"
          icon="📚"
          items={formationItems}
          selected={selectedFormations}
          onChange={v => { setSelectedFormations(v); setPage(1); }}
        />
        <MultiSelectFilter
          label="Formateur"
          icon="👤"
          items={formateurItems}
          selected={selectedFormateurs}
          onChange={v => { setSelectedFormateurs(v); setPage(1); }}
        />
        <MultiSelectFilter
          label="Client"
          icon="🏢"
          items={clientItems}
          selected={selectedClients}
          onChange={v => { setSelectedClients(v); setPage(1); }}
        />
        {(selectedFormations.length > 0 || selectedFormateurs.length > 0 || selectedClients.length > 0) && (
          <button
            onClick={() => { setSelectedFormations([]); setSelectedFormateurs([]); setSelectedClients([]); setPage(1); }}
            style={{ padding: '0.3rem 0.7rem', borderRadius: '20px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-tertiary)', fontSize: '0.78rem', cursor: 'pointer' }}>
            ✕ Réinitialiser les filtres
          </button>
        )}
      </div>

      {loading ? <Loading /> : (
        <>
          <DataTable
            columns={columns}
            data={data?.data}
            onRowClick={r => navigate(`/sessions/${r.session_id}`)}
            rowStyle={r => r.salle_couleur ? { boxShadow: `inset 4px 0 0 0 ${r.salle_couleur}` } : {}}
          />
          <Pagination page={page} total={data?.total || 0} limit={25} onChange={setPage} />
        </>
      )}

      {modal && (
        <ModalNouvelleSession
          onSave={() => { setModal(false); setRefresh(r => r + 1); }}
          onClose={() => setModal(false)}
        />
      )}
    </div>
  );
}
